import { MentionsTextField } from "@jackstenglein/mui-mentions";
import {
  Launch as LaunchIcon,
  OpenInFull as OpenInFullIcon,
  Send as SendIcon,
  Stop as StopIcon,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  alpha,
  Box,
  Button,
  Card,
  CardContent,
  capitalize,
  Divider,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import React, { useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router";
import { MARKDOWN_COLORS } from "@/theme/theme";
import { DiscoveryRunStateChip } from "../components/discovery-run-state-chip";
import { LiveView } from "../components/live-view";
import { MarkdownBlock } from "../components/markdown-block";
import { RunStatusChip } from "../components/run-status-chip";
import { ScenarioStateChip } from "../components/scenario-state-chip";
import { useApiMutation, useApiQuery } from "../hooks";
import { useMemberInfo } from "../hooks/use-member-info";
import type { AppConfig, DiscoveryRun, Persona, Scenario, Test, TestRun } from "../types";
import type { AutoAgentEvent } from "../types/agent-events";
import type {
  NewTestChatMessageSchema,
  TestChat,
  TestChatMessage,
  TestChatOption,
  TestChatUpdateSchema,
} from "../types/test-chat";
import { clickableProps } from "../utils/keyboard";
import { humanizeText } from "../utils/text";

type AgentMessageProps = {
  message: TestChatMessage;
};

type UserMessageProps = {
  message: TestChatMessage;
};

type ChatOptionProps = {
  option: TestChatOption;
  onClick: () => void;
};

const AgentMessage: React.FC<AgentMessageProps> = ({ message }) => {
  return (
    <Stack
      spacing={1}
      sx={{
        width: "100%",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "8px",
        p: 2,
        ":hover": {
          borderColor: "primary.main",
        },
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        {message.thoughts && (
          <Tooltip title={message.thoughts} placement="bottom-start">
            <Typography variant="body2" color="text.secondary">
              Thoughts...
            </Typography>
          </Tooltip>
        )}
        {message.goal && (
          <Tooltip title="This message by the agent is intended to achieve a specific goal.">
            <Typography variant="caption" color="text.secondary">
              {humanizeText(message.goal)}
            </Typography>
          </Tooltip>
        )}
      </Stack>
      <Stack spacing={2}>
        <MarkdownBlock content={message.content} />
        {message.live_view_url && (
          <Box
            sx={{
              width: "100%",
              maxWidth: "600px",
              alignSelf: "flex-start",
            }}
          >
            <LiveView liveViewUrl={message.live_view_url} height="300px" />
          </Box>
        )}
        <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            {capitalize(message.state)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Agent{" "}
            {formatDistanceToNow(message.ready_at ?? message.started_at ?? message.created_at, {
              addSuffix: true,
            })}
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

const UserMessage: React.FC<UserMessageProps> = ({ message }) => {
  const { getMember } = useMemberInfo();

  return (
    <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
      <Card sx={{ maxWidth: "600px", width: "100%" }}>
        <CardContent>
          <Stack spacing={2}>
            <MarkdownBlock content={message.content} />
            <Stack direction="row" spacing={1} alignItems="stretch" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">
                {getMember(message.created_by).name}{" "}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDistanceToNow(message.created_at, { addSuffix: true })}
              </Typography>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

const ChatOption: React.FC<ChatOptionProps> = ({ option, onClick }) => {
  return (
    <Card
      sx={{
        width: "250px",
        cursor: "pointer",
        ":hover": {
          backgroundColor: "grey.100",
          borderColor: "primary.main",
          borderWidth: 1,
        },
      }}
      {...clickableProps(onClick)}
      onClick={onClick}
    >
      <Stack
        spacing={0}
        sx={{ p: 1, flex: 1, height: "100%", textAlign: "center" }}
        alignItems="stretch"
        justifyContent="space-between"
      >
        <Typography variant="body2" sx={{ p: 2, textAlign: "center" }}>
          {option.name}
        </Typography>
        <Typography variant="caption" align="right" color="text.secondary">
          {option.value}
        </Typography>
      </Stack>
    </Card>
  );
};

export default function TestChatDetailsPage() {
  const params = useParams<{ chatId: string }>();
  const nav = useNavigate();
  const theme = useTheme();

  // Scroll anchor for auto-scrolling
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const chatId = params.chatId;

  const [message, setMessage] = React.useState<string>("");
  const [chatTitle, setChatTitle] = React.useState<string | undefined>(undefined);

  const {
    data: chat,
    isLoading,
    refetch: refetchChat,
  } = useApiQuery<TestChat>(`/test-chats/${chatId}`, {
    enabled: !!chatId,
  });

  const { data: messages, refetch: refetchMessages } = useApiQuery<TestChatMessage[]>(
    `/test-chats/${chat?.id}/messages`,
    {
      enabled: !!chat,
    },
  );

  const { mutate: update, isPending: isUpdating } = useApiMutation<
    TestChatUpdateSchema,
    TestChatMessage
  >("PATCH", `/test-chats/${chatId}`, {
    onSuccess: () => {
      setChatTitle(undefined);

      refetchChat();
    },
  });

  const { mutate: sendMessage, isPending: isSendingMessage } = useApiMutation<
    NewTestChatMessageSchema,
    TestChatMessage
  >("POST", `/test-chats/${chatId}/message`, {
    onSuccess: () => {
      refetchChat();
      refetchMessages();

      setMessage("");
    },
  });

  const { mutate: sendEvent, isPending: isSendingEvent } = useApiMutation<AutoAgentEvent, void>(
    "POST",
    `/test-chats/${chatId}/event`,
    {
      onSuccess: () => {
        refetchChat();
        refetchMessages();
      },
    },
  );

  const { data: personas } = useApiQuery<Persona[]>("/personas");

  const { data: test } = useApiQuery<Test>(`/tests/${chat?.dependency_id}`, {
    enabled: !!chat?.dependency_id,
  });

  const { data: config } = useApiQuery<AppConfig>(`/config/${chat?.app_config_id}`, {
    enabled: !!chat?.app_config_id,
  });

  const { data: scenario } = useApiQuery<Scenario>(`/scenarios/${chat?.entity_id}`, {
    enabled: !!chat?.entity_id && chat?.entity_type === "scenario",
  });

  const { data: run } = useApiQuery<TestRun>(`/runs/${chat?.entity_id}`, {
    enabled: !!chat?.entity_id && chat?.entity_type === "test_run",
  });

  const { data: dr } = useApiQuery<DiscoveryRun>(`/discovery-runs/${chat?.entity_id}`, {
    enabled: !!chat?.entity_id && chat?.entity_type === "discovery_run",
  });

  const usedPersona = useMemo(() => {
    if (!chat?.persona_id) return undefined;
    return personas?.find((p) => p.id === chat.persona_id);
  }, [chat?.persona_id, personas]);

  const options = useMemo(() => {
    if (!chat) return [];
    if (chat.state !== "draft") return [];
    if (!chat.initial_options) return [];
    return chat.initial_options;
  }, [chat]);

  // Auto-scroll to bottom when messages, chat state, or options change
  useEffect(() => {
    if (!["running", "thinking", "idle"].includes(chat?.state ?? "")) {
      return; // Only scroll when the chat is not running
    }

    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    // Use requestAnimationFrame to ensure DOM is painted
    const frameId = requestAnimationFrame(scrollToBottom);
    return () => cancelAnimationFrame(frameId);
  }, [chat?.state]);

  // Ensure scroll also occurs on initial load and after late paints (images/markdown)
  useEffect(() => {
    if (isLoading) return;
    const scrollNow = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: "auto" });
    };
    const rafId = requestAnimationFrame(scrollNow);
    const t1 = setTimeout(scrollNow, 200);
    const t2 = setTimeout(scrollNow, 600);
    return () => {
      cancelAnimationFrame(rafId);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [isLoading]);

  if (!chatId) {
    return (
      <Box data-testid="test-chat-details-page">
        <Typography variant="h6" color="error">
          Chat ID is missing in the URL parameters.
        </Typography>
      </Box>
    );
  }

  if (isLoading) {
    return (
      <Box data-testid="test-chat-details-page">
        <Typography variant="body1" color="text.secondary">
          Loading chat details...
        </Typography>
      </Box>
    );
  }

  if (!chat) {
    return (
      <Box data-testid="test-chat-details-page">
        <Typography variant="body1" color="text.secondary">
          Chat not found!!
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      data-testid="test-chat-details-page"
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: "1 1 0%",
        height: "100%",
        width: "100%",
        mt: -4,
      }}
    >
      <Box
        sx={{
          position: "sticky",
          top: 0,
          pt: 2,
          zIndex: 2,
          backgroundColor: "background.default",
        }}
      >
        <Stack
          direction="row"
          spacing={2}
          alignItems="flex-start"
          justifyContent="space-between"
          mb={2}
        >
          {chatTitle ? (
            <TextField
              variant="outlined"
              size="small"
              value={chatTitle}
              disabled={isUpdating}
              onChange={(e) => {
                setChatTitle(e.target.value);
              }}
              onBlur={() => setChatTitle(undefined)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  update({ title: chatTitle });
                }
              }}
              autoFocus
              sx={{ flex: 1, maxWidth: "600px" }}
            />
          ) : (
            <Tooltip title={`Edit chat title, the agent is in ${chat.state} state`}>
              <Typography
                variant="h5"
                {...clickableProps(() => setChatTitle(chat.title))}
                onClick={() => setChatTitle(chat.title)}
                sx={{ cursor: "pointer" }}
              >
                {chat.title}
              </Typography>
            </Tooltip>
          )}
        </Stack>
        <Divider />
      </Box>
      <Box
        sx={{
          display: "flex",
          height: "100%",
          position: "relative",
          flexDirection: "column",
          flex: "1 1 0%",
          width: "100%",
          maxWidth: "1000px",
          m: 2,
          alignSelf: "center",
        }}
      >
        <Stack
          direction="column"
          alignItems="stretch"
          justifyContent="stretch"
          spacing={2}
          sx={{
            flex: 1,
            overflowY: "auto",
          }}
        >
          {messages?.length === 0 && (
            <Stack
              direction="column"
              alignItems="center"
              justifyContent="center"
              sx={{ flex: 1, p: 2 }}
            >
              <Typography variant="body1" color="text.secondary">
                This chat has no messages yet!
              </Typography>
            </Stack>
          )}
          {messages?.map((message) => {
            if (message.sender === "user") {
              return <UserMessage key={message.id} message={message} />;
            } else if (message.sender === "agent") {
              return <AgentMessage key={message.id} message={message} />;
            }
            return null;
          })}

          {/* Current State and Scenario Info */}
          {!["idle", "draft"].includes(chat.state) && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 2,
              }}
            >
              <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{
                  px: 3,
                  py: 1.5,
                  backgroundColor: "background.default",
                  borderRadius: 3,
                  borderWidth: 1,
                  borderStyle: "solid",
                  borderColor: "divider",
                }}
              >
                {["thinking", "running"].includes(chat.state) && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "primary.main",
                        animation: "pulse 2s infinite",
                        "@keyframes pulse": {
                          "0%": {
                            opacity: 1,
                            transform: "scale(1)",
                          },
                          "50%": {
                            opacity: 0.5,
                            transform: "scale(1.1)",
                          },
                          "100%": {
                            opacity: 1,
                            transform: "scale(1)",
                          },
                        },
                      }}
                    />
                    <Typography variant="body2" sx={{ color: "grey.800" }}>
                      Agent is {chat.state === "thinking" ? "thinking" : "working"}...
                    </Typography>
                  </Stack>
                )}

                {chat.state === "stopped" && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "warning.main",
                      }}
                    />
                    <Typography variant="body2" sx={{ color: "grey.800" }}>
                      Agent stopped
                    </Typography>
                  </Stack>
                )}

                {chat.state === "error" && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "warning.main",
                      }}
                    />
                    <Typography variant="body2" sx={{ color: "grey.800" }}>
                      Agent errored
                    </Typography>
                  </Stack>
                )}

                {chat.state === "done" && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "success.main",
                      }}
                    />
                    <Typography variant="body2" sx={{ color: "grey.800" }}>
                      Agent completed successfully
                    </Typography>
                  </Stack>
                )}

                {chat.state === "waiting" && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: "warning.main",
                      }}
                    />
                    <Typography variant="body2" sx={{ color: "grey.800" }}>
                      Agent is waiting for your input
                    </Typography>
                  </Stack>
                )}
              </Stack>
            </Box>
          )}
          {/* Scroll target for auto-scroll functionality */}
          <div ref={messagesEndRef} />
        </Stack>
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            width: "100%",
            alignSelf: "center",
            mt: 1,
            pb: 1,
            backgroundColor: "background.default",
            borderRadius: "8px",
            mb: -4,
          }}
        >
          {!["done", "error"].includes(chat.state) && options.length > 0 && (
            <Stack
              sx={{ pb: 1 }}
              direction="row"
              spacing={1}
              alignItems="stretch"
              justifyContent="center"
            >
              {options.map((option) => (
                <ChatOption
                  key={option.id}
                  option={option}
                  onClick={() => {
                    sendMessage({
                      message: "",
                      selected_option: {
                        is_initial: true,
                        option_id: option.id,
                      },
                    });
                  }}
                />
              ))}
            </Stack>
          )}
          {(chat.preview_url || chat.recording_url) && (
            <Accordion
              sx={{
                borderRadius: "8px",
                border: "1px solid",
                borderColor: "primary.main",
                mb: 1,
              }}
            >
              <AccordionSummary expandIcon={<OpenInFullIcon fontSize="small" />}>
                <Stack direction="row" spacing={1} alignItems="center">
                  {scenario && <ScenarioStateChip state={scenario.state} size="small" />}
                  {run && (
                    <RunStatusChip status={run.final_run_status ?? run.run_status} size="small" />
                  )}
                  {dr && <DiscoveryRunStateChip state={dr.state} size="small" />}
                  <Typography variant="subtitle2">
                    {chat.recording_url ? "Recording" : "Live View"}
                  </Typography>
                </Stack>
              </AccordionSummary>
              <AccordionDetails>
                {chat.preview_url && !chat.recording_url && (
                  <LiveView liveViewUrl={chat.preview_url} height="400px" />
                )}
                {chat.recording_url && (
                  <Box
                    sx={{
                      width: "100%",
                      alignSelf: "center",
                      display: "flex",
                      justifyContent: "center",
                    }}
                  >
                    <video
                      controls
                      autoPlay
                      muted
                      style={{
                        height: "400px",
                        objectFit: "cover",
                        borderRadius: "8px",
                        border: `1px solid ${theme.palette.grey[400]}`,
                      }}
                    >
                      <source
                        src={chat.recording_url}
                        type={`video/${chat.recording_url.split("?")[0].split(".").pop()}`}
                      />
                      Your browser does not support the video tag.
                    </video>
                  </Box>
                )}
              </AccordionDetails>
            </Accordion>
          )}
          <Card
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 2,
              boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
              backgroundColor: "common.white",
              "&:focus-within": {
                borderColor: "primary.main",
                boxShadow: "0 0 0 2px rgba(25, 118, 210, 0.12)",
              },
            }}
          >
            <Stack
              direction="column"
              spacing={1}
              sx={{
                p: 1.5,
              }}
            >
              {["done", "error"].includes(chat.state) ? (
                <Alert severity={chat.state === "done" ? "success" : "error"} sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    This chat is in {chat.state} state, you can not send new messages. Please create
                    a new chat!
                  </Typography>
                </Alert>
              ) : (
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <MentionsTextField
                    dataSources={[
                      {
                        data: (personas ?? []).map((p) => ({
                          id: `/personas/${p.id}`,
                          display: `Persona: ${p.name}`,
                        })),
                      },
                    ]}
                    highlightColor={MARKDOWN_COLORS.highlightBg}
                    autoFocus
                    fullWidth
                    multiline
                    variant="outlined"
                    size="small"
                    value={message}
                    onChange={(e) => setMessage(e)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                        e.preventDefault();

                        // Handle different button states
                        if (["thinking", "running"].includes(chat.state)) {
                          // Stop action
                          if (!isSendingEvent) {
                            sendEvent({
                              data: {
                                type: "stop",
                                target: "agent",
                              },
                            });
                          }
                        } else if (["stopped"].includes(chat.state)) {
                          // Resume action
                          if (!isSendingEvent) {
                            sendEvent({
                              data: {
                                type: "start",
                                target: "agent",
                              },
                            });
                          }
                        } else if (message.trim() && !isSendingMessage) {
                          // Send message
                          sendMessage({
                            message,
                            selected_option: undefined,
                          });
                        }
                      }
                    }}
                    disabled={isSendingMessage || ["done", "error"].includes(chat.state)}
                    placeholder="Type your message here. Press Enter for new line and Ctrl/Cmd + Enter to send. Use @ for mentions."
                    minRows={2}
                    maxRows={8}
                    autoComplete="off"
                    InputProps={{
                      disableUnderline: true,
                      sx: {
                        borderRadius: 3,
                        "& fieldset": { border: "none" },
                        fontSize: 16,
                        fontWeight: 400,
                        color: theme.palette.text.primary,
                        alignItems: "center",
                        "& .MuiInputBase-inputMultiline": {
                          display: "flex",
                          alignItems: "center",
                          minHeight: "24px",
                        },
                        "& textarea::placeholder": {
                          color: alpha(theme.palette.text.secondary, 0.6),
                          opacity: 1,
                        },
                      },
                    }}
                  />
                  {["thinking", "running"].includes(chat.state) && (
                    <Tooltip title="Stop">
                      <Button
                        variant="contained"
                        size="small"
                        disabled={isSendingEvent}
                        onClick={() => {
                          sendEvent({
                            data: {
                              type: "stop",
                              target: "agent",
                            },
                          });
                        }}
                        sx={{
                          minWidth: 40,
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          p: 0,
                          color: "common.white",
                          backgroundColor: "grey.800",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: "primary.main",
                            color: "common.white",
                          },
                          "&:disabled": {
                            backgroundColor: "grey.100",
                            color: "grey.500",
                          },
                        }}
                      >
                        <StopIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  )}
                  {["waiting", "idle", "draft", "stopped"].includes(chat.state) && (
                    <Tooltip title="Send message (or Ctrl/Cmd + Enter)">
                      <Button
                        variant="contained"
                        size="small"
                        disabled={
                          isSendingMessage ||
                          (["thinking", "running"].includes(chat.state) &&
                            !["paused"].includes(scenario?.state ?? "")) ||
                          (!["stopped"].includes(chat.state) && !message.trim())
                        }
                        onClick={() => {
                          if (chat.state === "stopped") {
                            sendEvent({
                              data: {
                                type: "start",
                                target: "agent",
                              },
                            });
                          } else {
                            sendMessage({
                              message,
                              selected_option: undefined,
                            });
                          }
                        }}
                        sx={{
                          minWidth: 40,
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          p: 0,
                          color: message.trim() ? "common.white" : "grey.500",
                          backgroundColor: message.trim() ? "grey.800" : "grey.100",
                          transition: "all 0.2s ease",
                          "&:hover": {
                            backgroundColor: message.trim() ? "primary.main" : "background.default",
                            color: message.trim() ? "common.white" : "grey.800",
                          },
                          "&:disabled": {
                            backgroundColor: "grey.100",
                            color: "grey.500",
                          },
                        }}
                      >
                        <SendIcon fontSize="small" />
                      </Button>
                    </Tooltip>
                  )}
                </Stack>
              )}
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  minHeight: "32px",
                  px: 1,
                  borderTop: "1px solid",
                  borderColor: "divider",
                  backgroundColor: "background.default",
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  {test && (
                    <Tooltip title="Dependency Test">
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          nav(`/tests-main/tests/${test.id}`);
                        }}
                        endIcon={<LaunchIcon fontSize="small" />}
                        sx={{
                          fontSize: "0.75rem",
                          color: "grey.800",
                          "&:hover": {
                            backgroundColor: "background.default",
                            color: "primary.main",
                          },
                        }}
                      >
                        {test.name}
                      </Button>
                    </Tooltip>
                  )}
                  {config && (
                    <Tooltip title="Application configuration used in this chat">
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          nav("/configuration?tab=config");
                        }}
                        endIcon={<LaunchIcon fontSize="small" />}
                        sx={{
                          fontSize: "0.75rem",
                          color: "grey.800",
                          "&:hover": {
                            backgroundColor: "background.default",
                            color: "primary.main",
                          },
                        }}
                      >
                        {config.name}
                      </Button>
                    </Tooltip>
                  )}
                  {usedPersona && (
                    <Tooltip title="Used Persona">
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          nav(`/personas/${usedPersona.id}`);
                        }}
                        endIcon={<LaunchIcon fontSize="small" />}
                        sx={{
                          fontSize: "0.75rem",
                          color: "grey.800",
                          "&:hover": {
                            backgroundColor: "background.default",
                            color: "primary.main",
                          },
                        }}
                      >
                        {usedPersona.name}
                      </Button>
                    </Tooltip>
                  )}
                  {scenario && (
                    <Tooltip title="View Scenario">
                      <Button
                        variant="text"
                        size="small"
                        onClick={() => {
                          nav(`/tests-main/scenarios/${scenario.id}`);
                        }}
                        endIcon={<LaunchIcon fontSize="small" />}
                        sx={{
                          fontSize: "0.75rem",
                          color: "grey.800",
                          "&:hover": {
                            backgroundColor: "background.default",
                            color: "primary.main",
                          },
                        }}
                      >
                        {scenario.name}
                      </Button>
                    </Tooltip>
                  )}
                  {run && (
                    <Tooltip title="View Test Run">
                      <Button
                        variant="text"
                        size="small"
                        color="inherit"
                        onClick={() => {
                          nav(`/runs/${run.id}`);
                        }}
                        endIcon={<LaunchIcon fontSize="small" />}
                      >
                        {run.name}
                      </Button>
                    </Tooltip>
                  )}
                  {dr && (
                    <Tooltip title="View Discovery Run">
                      <Button
                        variant="text"
                        size="small"
                        color="inherit"
                        onClick={() => {
                          nav(`/discovery-runs/${dr.id}`);
                        }}
                        endIcon={<LaunchIcon fontSize="small" />}
                      >
                        {`See ${chat.intent ?? ""} details (${dr.discovered_flows_count} / ${dr.flow_count})`}
                      </Button>
                    </Tooltip>
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {message.trim() ? `${message.length} chars` : "0 chars"}
                </Typography>
              </Stack>
            </Stack>
          </Card>
        </Box>
      </Box>
    </Box>
  );
}
